import Marker, { MarkerProps } from 'components/elements/maps/Marker'
import Text from 'components/elements/texts/Text'
import {
  CoordsProps,
  LocationConfirmStateProps,
  MAP_LANGUAGE,
  MAP_OPTIONS,
  MAP_REGION,
  MAP_ZOOM_ON_CLICK_MARKER,
  SearchResultPlaceProps,
  SELECT_PLACE_MAP_DEFAULT_ZOOM,
} from 'constants/map'
import { APIKeyContext } from 'contexts/APIKeyContext'
import GoogleMapReact, { fitBounds } from 'google-map-react'
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { convertToCoordsFromLatLng, formatAddress } from 'utils/map'

import AddressArea from './AddressArea'
import HelpButton from './HelpButton'
import HelpLayer from './HelpLayer'
import SearchArea from './SearchArea'
import StartLocation from './StartLocation'
import PrimaryButton from '../../../elements/buttons/PrimaryButton'
import WrappedTelIcon from 'components/elements/icons/WrappedTelIcon'
import CallModal from 'components/elements/modal/CallModal'
import Button from 'components/elements/buttons/Button'
import { isSjDomain, isKsDomain } from 'utils/domain'

enum HelpTypes {
  none = 0,
  map,
  noGps,
  description,
  help,
}

/**
 * 場所選択マップ
 */
const MapArea: React.FC<MapAreaProps> = (props) => {
  const { mapHeight, usingGeolocation } = props
  const MapDefaultLat = 35.681236
  const MapDefaultLng = 139.767125

  const currentCoords: CoordsProps = {
    // 初期値 は 東京駅 に設定
    lat: MapDefaultLat,
    lng: MapDefaultLng,
  }
  const [apiKey] = useContext(APIKeyContext)

  // 選択した場所のState
  const [coords, setCoords] = useState(currentCoords)
  const [address, setAddress] = useState(props.location.address)
  const [locationName, setLocationName] = useState(props.location.locationName)

  // マップのState
  const [center, setCenter] = useState(currentCoords)
  const [zoom, setZoom] = useState(SELECT_PLACE_MAP_DEFAULT_ZOOM)

  // 管理用State
  const [currentLocation, setCurrentLocation] = useState(props.location)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const mapAreaRef = useRef<HTMLDivElement>(null)
  const [addressInfo, setAddressInfo] = useState('')
  const [searchResultPlaces, setSearchResultPlaces] = useState<
    Array<SearchResultPlaceProps>
  >([])
  const [visibleInfoWindow, setVisibleInfoWindow] = useState<
    google.maps.InfoWindow
  >()
  const [map, setMap] = useState<google.maps.Map>()
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder>()
  const [marker, setMarker] = useState<google.maps.Marker>()
  const [searchBox, setSearchBox] = useState<google.maps.places.SearchBox>()
  const [isGeolocationAcquired, setIsGeolocationAcquired] = useState(false)
  const [hasGpsInfo, setHasGpsInfo] = useState(false)

  // モーダル系
  const [helpType, setHelpType] = useState(
    usingGeolocation ? HelpTypes.none : HelpTypes.description
  )

  // 座標から住所を取得
  // （リクエスト送信をしているため、時間差を考慮してStateの更新を別のuseEffectから監視）
  const fetchAddress = useCallback(
    (targetCoords: CoordsProps): void => {
      const geo = geocoder ?? new google.maps.Geocoder()
      const beforeCoords = { lat: coords.lat, lng: coords.lng }
      geo.geocode({ location: targetCoords }, (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK || !results[0].geometry) {
          // 住所が取得できなかった場合は、1つ前の地点へ戻す
          setCoords(beforeCoords)
          marker?.setPosition(beforeCoords)
          return
        }
        const address = results[0].formatted_address
        setAddress(formatAddress(address))

        const addressComponents = results[0].address_components
        const isDomestic = addressComponents.some(
          (val: google.maps.GeocoderAddressComponent): boolean =>
            val.short_name === 'JP'
        )
        props.setIsDisabled(!isDomestic)
        return
      })
    },
    [map, marker, geocoder, address, coords.lat, coords.lng, setGeocoder]
  )

  const clearSearchResultPlaces = (): void => {
    searchResultPlaces.forEach((searchResultPlace) => {
      google.maps.event.clearListeners(searchResultPlace.controlMarker, 'click')
      searchResultPlace.controlMarker.setMap(null)
      searchResultPlace.infoWindow.close()
    })
    setSearchResultPlaces([])
  }

  const closeInfoWindow = (): void => {
    if (visibleInfoWindow) {
      visibleInfoWindow.close()
    }
  }

  // GPS取得
  useEffect(() => {
    if (
      !map ||
      !marker ||
      !navigator.geolocation ||
      !usingGeolocation ||
      isGeolocationAcquired
    ) {
      return
    }

    setIsGeolocationAcquired(true)

    navigator.geolocation.getCurrentPosition(
      (position): void => {
        const currentCoords: CoordsProps = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }
        setCurrentLocation({
          ...props.location,
          ...currentCoords,
          address: '',
          locationName: '',
        })
        setCoords(currentCoords)
        setLocationName('')
        fetchAddress(currentCoords)
        setCenter(currentCoords)
        marker.setPosition(currentCoords)
        setHasGpsInfo(true)
        setHelpType(HelpTypes.map)
      },
      (): void => {
        // GPSが取得できない場合は日本地図表示
        const boundsResult = fitBounds(
          {
            ne: {
              lat: 45.68168656167227,
              lng: 145.71696570680248,
            },
            sw: {
              lat: 26.099289597125267,
              lng: 127.3349149941409,
            },
          },
          {
            width: window.innerWidth,
            height: window.innerHeight / 2,
          }
        )
        setCenter(boundsResult.center)
        setZoom(boundsResult.zoom)
        setHelpType(HelpTypes.noGps)
      }
    )
  }, [
    map,
    marker,
    usingGeolocation,
    isGeolocationAcquired,
    fetchAddress,
    props.location,
    setGeocoder,
  ])

  // 「現在地取得」をクリックしたときの動作
  const onClickCurrentLocation = useCallback(() => {
    /** navigator.geolocation.getCurrentPositionから最新の位置情報を取得する */
    navigator.geolocation.getCurrentPosition(
      (position): void => {
        /**
         * GPS取得に成功したので以下を実行
         * ・ローカルステートに取得した最新のGPS情報を設定する
         * ・マップの拡大縮小の比率をデフォルトに設定
         * ・車のマーカー位置を最新の位置情報に移動する
         * ・最新のGPSから住所名を取得する
         */

        /** GPSから取得した最新の位置情報 */
        const latestPlaceCoords: CoordsProps = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }
        setCurrentLocation({
          lat: latestPlaceCoords.lat,
          lng: latestPlaceCoords.lng,
          address: '',
          isSelected: false,
          locationName: '',
        })
        setCoords(latestPlaceCoords)
        setLocationName('')
        setCenter(latestPlaceCoords)
        setZoom(SELECT_PLACE_MAP_DEFAULT_ZOOM)
        marker?.setPosition(latestPlaceCoords)
        fetchAddress(latestPlaceCoords)
        clearSearchResultPlaces()
        closeInfoWindow()
      },
      (): void => {
        /**
         * GPS取得に失敗したので以下を実行
         * ・現在地取得」ボタンを非表示、
         * ・現在地取得に失敗した旨のヘルプレイヤ表示
         */
        setHasGpsInfo(false)
        setHelpType(HelpTypes.noGps)
      }
    )
  }, [currentLocation, props.location, visibleInfoWindow])

  // マーカーをドラッグ中の動作
  const onDragMarker = useCallback(
    (ev: any) => {
      const currentCoords = convertToCoordsFromLatLng(ev.latLng)
      setCoords(currentCoords)
      props.setIsDisabled(true)
    },
    [map, marker, geocoder, props.location]
  )

  // マーカーのドラッグ終了時の動作
  const onDragEndMarker = useCallback(
    (ev: any) => {
      const currentCoords = convertToCoordsFromLatLng(ev.latLng)
      setCoords(currentCoords)
      setLocationName('')
      setCenter(currentCoords)
      fetchAddress(currentCoords)
      closeInfoWindow()
    },
    [map, marker, geocoder, props.location, visibleInfoWindow]
  )

  // マップ上をクリックしたときの動作
  const onClickMap = useCallback(
    (ev: any) => {
      const currentCoords = convertToCoordsFromLatLng(ev.latLng)
      setCoords(currentCoords)
      setLocationName('')
      setCenter(currentCoords)
      marker?.setPosition(currentCoords)
      fetchAddress(currentCoords)
      closeInfoWindow()
    },
    [map, marker, geocoder, props.location, visibleInfoWindow]
  )

  // マップを拡大縮小、移動したときの動作
  const onBoundsChangedMap = useCallback(() => {
    if (!map || !searchBox) {
      return
    }
    const mapBounds = map.getBounds()
    // 検索範囲の調整
    mapBounds && searchBox.setBounds(mapBounds)
  }, [map, marker, geocoder, searchBox, center, zoom])

  const setSearchResultPlace: (
    targetSearchResultPlace: SearchResultPlaceProps
  ) => void = (targetSearchResultPlace) => {
    const targetCoords = {
      lat: targetSearchResultPlace.lat,
      lng: targetSearchResultPlace.lng,
    }

    // マーカー、表示範囲の移動
    setCoords(targetCoords)
    setLocationName(targetSearchResultPlace.locationName)
    setAddress(formatAddress(targetSearchResultPlace.address))
    setCenter(targetCoords)
    fetchAddress(targetCoords)
    setZoom(MAP_ZOOM_ON_CLICK_MARKER)
    marker?.setPosition(targetCoords)

    // InfoWindowの表示
    closeInfoWindow()
    targetSearchResultPlace.infoWindow.open(map, marker)
    setVisibleInfoWindow(targetSearchResultPlace.infoWindow)
  }

  // 検索時（検索サジェストの選択 or 検索入力欄でエンター or 検索アイコンタップ）
  const onPlacesChangedSearchBox = useCallback(() => {
    const places = searchBox?.getPlaces()
    searchInputRef.current?.blur()
    if (helpType !== HelpTypes.none) {
      if (helpType === HelpTypes.description && usingGeolocation) {
        // GPS使用時で説明表示中なら地図のヘルプを出す
        setHelpType(HelpTypes.map)
      } else {
        // ヘルプレイヤ非表示
        setHelpType(HelpTypes.none)
      }
    }

    if (!places || places.length === 0) {
      setAddressInfo('検索結果が0件でした。')
      clearSearchResultPlaces()
      props.setIsDisabled(true)
      return
    }

    if (props.usingMultiSearch) {
      const tempSearchResultPlaces: Array<SearchResultPlaceProps> = []
      const searchResultPlacesBounds = new google.maps.LatLngBounds()
      places.forEach((place) => {
        if (!place.geometry || !place.formatted_address || !place.name) {
          return
        }

        const targetCoords = convertToCoordsFromLatLng(place.geometry.location)

        // タップ時に開くウィンドウ
        const infoWindow = new google.maps.InfoWindow({
          content: place.name,
          disableAutoPan: true,
        })

        // Google Map純正InfoWindowを用いるため、透過したGoogle Map純正Markerを用いる
        const controlMarker = new google.maps.Marker({
          map: map,
          icon: {
            // 1px*1pxの透過画像をアイコン分に引き伸ばし、タップ可能範囲を広げる
            url:
              'data:image/gif;base64,R0lGODlhAQABAGAAACH5BAEKAP8ALAAAAAABAAEAAAgEAP8FBAA7',
            scaledSize: new google.maps.Size(26, 26),
          },
          title: place.name,
          position: place.geometry.location,
          zIndex: 1,
        })

        // 描画用にgoogle-map-react版Markerを用いる
        const renderingMarker: MarkerProps = {
          component: props.searchResultMarkerComponent
            ? props.searchResultMarkerComponent
            : props.addressComponent,
          ...targetCoords,
        }

        tempSearchResultPlaces.push({
          infoWindow,
          controlMarker,
          renderingMarker,
          ...targetCoords,
          address: place.formatted_address,
          locationName: place.name,
        })

        // 表示範囲の調整用
        if (place.geometry.viewport) {
          searchResultPlacesBounds.union(place.geometry.viewport)
        } else {
          searchResultPlacesBounds.extend(place.geometry.location)
        }
      })

      setSearchResultPlaces(tempSearchResultPlaces)
      map?.fitBounds(searchResultPlacesBounds)
      // 検索結果が1件の場合は検索結果マーカーを住所に設定
      if (tempSearchResultPlaces.length === 1) {
        setSearchResultPlace(tempSearchResultPlaces[0])
        props.setIsDisabled(false)
      }
    } else {
      props.setIsDisabled(false)
      const target = places.find((place) => place.formatted_address)

      if (!target || !target.geometry || !target.formatted_address) {
        setAddressInfo('キーワードに合致する場所が特定できません。')
        return
      }
      const targetCoords = convertToCoordsFromLatLng(target.geometry.location)
      fetchAddress(targetCoords)
      setCoords(targetCoords)
      setLocationName(target.name)
      setAddress(target.formatted_address)
      setCenter(targetCoords)
      marker?.setPosition(targetCoords)
    }
  }, [
    map,
    marker,
    geocoder,
    searchBox,
    helpType,
    props.location,
    visibleInfoWindow,
  ])

  // 検索結果マーカークリック時（複数検索の場合のみ）
  const onClickSearchResultPlace = useCallback(
    (index: number) => {
      const targetSearchResultPlace = searchResultPlaces[index]
      setSearchResultPlace(targetSearchResultPlace)
    },
    [map, marker, geocoder, searchBox, searchResultPlaces, visibleInfoWindow]
  )

  // Google Map Marker系イベントリスナー
  useEffect(() => {
    if (!marker) {
      return
    }
    google.maps.event.addListener(marker, 'drag', onDragMarker)
    google.maps.event.addListener(marker, 'dragend', onDragEndMarker)

    return (): void => {
      google.maps.event.clearListeners(marker, 'drag')
      google.maps.event.clearListeners(marker, 'dragend')
    }
  }, [
    map,
    fetchAddress,
    visibleInfoWindow,
    marker,
    onDragMarker,
    onDragEndMarker,
  ])

  // Google Map系イベントリスナー
  useEffect(() => {
    if (!map) {
      return
    }
    google.maps.event.addListener(map, 'click', onClickMap)

    google.maps.event.addListener(map, 'bounds_changed', onBoundsChangedMap)

    return (): void => {
      google.maps.event.clearListeners(map, 'click')
      google.maps.event.clearListeners(map, 'bounds_changed')
    }
  }, [marker, map])

  // Google Map SearchBox系イベントリスナー
  useEffect(() => {
    if (!searchBox) {
      return
    }
    google.maps.event.addListener(searchBox, 'places_changed', () => {
      onPlacesChangedSearchBox()
      setZoom(MAP_ZOOM_ON_CLICK_MARKER)
    })

    return (): void => {
      google.maps.event.clearListeners(searchBox, 'places_changed')
    }
  }, [searchBox, fetchAddress, onPlacesChangedSearchBox, visibleInfoWindow])

  // 検索結果Google Map Marker系イベントリスナー
  useEffect(() => {
    searchResultPlaces.forEach((searchResultPlace, index) => {
      google.maps.event.addListener(
        searchResultPlace.controlMarker,
        'click',
        () => {
          onClickSearchResultPlace(index)
        }
      )
    })

    return (): void => {
      searchResultPlaces.forEach((searchResultPlace) => {
        google.maps.event.clearListeners(
          searchResultPlace.controlMarker,
          'click'
        )
      })
    }
  }, [searchResultPlaces, visibleInfoWindow])

  const onGoogleApiLoaded = useCallback((map: google.maps.Map): void => {
    // google-map-react v2.1.8のMarkerのonChildMouseDown系は
    // タッチデバイスに未対応のため、ドラッグ用に透明なGoogle Map純正Markerを用いる
    setMarker(
      new google.maps.Marker({
        position: {
          lat: props.location.lat,
          lng: props.location.lng,
        },
        map,
        draggable: true,
        zIndex: 10,
        icon: {
          // 1px*1pxの透過画像をアイコン分に引き伸ばし、タップ可能範囲を広げる
          url:
            'data:image/gif;base64,R0lGODlhAQABAGAAACH5BAEKAP8ALAAAAAABAAEAAAgEAP8FBAA7',
          scaledSize: new google.maps.Size(35, 35),
        },
      })
    )
    setMap(map)
    setGeocoder(new google.maps.Geocoder())
    const mapBounds = map.getBounds()
    searchInputRef.current &&
      mapBounds &&
      setSearchBox(
        new google.maps.places.SearchBox(searchInputRef.current, {
          bounds: mapBounds,
        })
      )
  }, [])

  // 住所は時間差で取得されるためuseEffectで監視し、親Stateを更新する方式とする
  useEffect(() => {
    props.setLocation({
      ...props.location,
      ...coords,
      address: formatAddress(address),
      locationName: props.usingLocationName ? locationName : '',
    })
    setAddressInfo('')
    props.setIsDisabled(true) // 時間差で住所取得&国内判定を行うので、デフォルトで決定ボタンを非活性状態にする
  }, [coords, address, locationName, props.setLocation])

  // マップの移動等を追跡してstateを更新
  // マップをドラッグで移動→setCenterで現在地の位置がcenterに来ない問題の対策
  const onMapChange = useCallback(
    ({ center, zoom }: { center: CoordsProps; zoom: number }): void => {
      setCenter(center)
      setZoom(zoom)
    },
    [center, zoom]
  )

  const onCloseHelpLayer = useCallback(() => {
    switch (helpType) {
      case HelpTypes.noGps:
        setHelpType(HelpTypes.description)
        break
      case HelpTypes.map:
      case HelpTypes.help:
        setHelpType(HelpTypes.none)
        break
    }
  }, [helpType])

  const onHelpButtonClick = useCallback(() => {
    setHelpType(HelpTypes.help)
  }, [])

  return (
    <React.Fragment>
      <SearchArea searchInputRef={searchInputRef} />
      <StyledMapArea ref={mapAreaRef}>
        <StyledMapWrapper mapHeight={props.mapHeight}>
          {apiKey && (
            <GoogleMapReact
              bootstrapURLKeys={{
                key: apiKey,
                language: MAP_LANGUAGE,
                region: MAP_REGION,
                libraries: 'places',
              }}
              onChange={onMapChange}
              center={center}
              zoom={zoom || SELECT_PLACE_MAP_DEFAULT_ZOOM}
              yesIWantToUseGoogleMapApiInternals
              options={MAP_OPTIONS}
              onGoogleApiLoaded={({ map }): void => onGoogleApiLoaded(map)}
            >
              {searchResultPlaces &&
                searchResultPlaces.map((searchResultPlace, index) => {
                  return (
                    <Marker
                      {...searchResultPlace.renderingMarker}
                      key={index}
                    />
                  )
                })}
              {props.fixedMarkers &&
                props.fixedMarkers.map((marker, index) => {
                  return <Marker {...marker} key={index} />
                })}
              {/* google-map-react v2.1.8版Markerは
              ドラッグに対応していないため、描画用途として用いる */}
              <Marker
                component={props.markerComponent}
                lat={props.location.lat}
                lng={props.location.lng}
              />
            </GoogleMapReact>
          )}
        </StyledMapWrapper>
        {hasGpsInfo && (
          <StartLocation
            onClickStartLocation={onClickCurrentLocation}
            text="現在地取得"
          />
        )}
        {usingGeolocation ? (
          <StyledHelpButton onClick={onHelpButtonClick}>
            <WrappedTelIcon />
          </StyledHelpButton>
        ) : (
          <HelpButton />
        )}
      </StyledMapArea>
      <AddressArea
        locationName={locationName}
        address={address}
        addressComponent={props.addressComponent}
        addressInfo={addressInfo}
      />
      {
        <HelpLayer
          isVisible={helpType !== HelpTypes.none}
          top={mapAreaRef.current?.getBoundingClientRect().top}
        >
          <HelpContainer height={mapHeight}>
            {helpType === HelpTypes.map ? (
              <>
                <PopoverArea marginRight={64} marginBottom={10} blowingTop={42}>
                  地図上をタッチすることで車のアイコンが移動します。
                  <br />
                  車のアイコンを長押しすると直接移動させることが可能です。
                </PopoverArea>
                <PopoverArea arrow="right" marginRight={64} marginBottom={10}>
                  拡大・縮小は右下のボタンまたは2本の指でタッチし、指を広げる・閉じることで操作が可能です。
                </PopoverArea>
              </>
            ) : helpType === HelpTypes.noGps || helpType === HelpTypes.help ? (
              <>
                <PopoverArea arrow="top" marginTop={16}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {helpType === HelpTypes.noGps && (
                      <span>
                        位置情報が取得できませんでした。
                        <br />
                      </span>
                    )}
                    {
                      <span>
                        <RedText>店舗名やお分かりになる範囲の住所</RedText>
                        を入力することで検索が可能です。
                        <br />
                        例：●●（コンビニ名）○○1丁目店
                      </span>
                    }
                  </div>
                </PopoverArea>
                <PopoverArea marginRight={64} marginBottom={10} blowingTop={58}>
                  車のアイコンが表示されている住所が取得されます。
                </PopoverArea>
              </>
            ) : (
              helpType === HelpTypes.description && (
                <>
                  <PopoverArea arrow="top" marginTop={16} marginRight={70}>
                    <RedText>店舗や住所</RedText>を入力してください
                  </PopoverArea>
                  {usingGeolocation ? (
                    <StyledHelpButton
                      onClick={(): void => setHelpType(HelpTypes.help)}
                    >
                      <WrappedTelIcon />
                    </StyledHelpButton>
                  ) : (
                    <HelpButton />
                  )}
                </>
              )
            )}
          </HelpContainer>
          <HelpFooter>
            {helpType === HelpTypes.help && (
              <CallModal isTransitionReAccess>
                <Button background="red" color="white">
                  コールセンターに電話をかける
                </Button>
              </CallModal>
            )}
            {helpType !== HelpTypes.description && (
              <PrimaryButton
                onClick={onCloseHelpLayer}
                withArrow={isSjDomain() || isKsDomain()}
              >
                説明を閉じる
              </PrimaryButton>
            )}
          </HelpFooter>
        </HelpLayer>
      }
    </React.Fragment>
  )
}

export type MapAreaProps = {
  // trueの場合、検索時に場所名を取得する
  usingLocationName?: boolean
  // trueの場合、場所未選択の初期表示時にGPS取得を行う
  usingGeolocation?: boolean
  // trueの場合は複数検索とし、全ての検索結果をマーカーとして地図上に表示する
  // falseの場合は単一検索とし、検索結果の1つ目の結果の位置を選択する
  usingMultiSearch?: boolean
  // 地図本体の高さを指定する（未指定の場合は 50vh）
  mapHeight?: string
  // ログスタックに用いるタイトル
  title: string
  location: LocationConfirmStateProps
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>
  setLocation: (location: LocationConfirmStateProps) => void
  // 固定表示用のマーカー
  fixedMarkers?: Array<MarkerProps>
  // マーカーとして表示されるアイコン
  markerComponent: React.ReactNode
  // 検索結果マーカーとして表示されるアイコン
  searchResultMarkerComponent?: React.ReactNode
  // 住所表示欄の左に表示されるアイコン
  addressComponent: React.ReactNode
}

const StyledMapArea = styled.div`
  position: relative;
`

const StyledMapWrapper = styled.div<{
  mapHeight?: string
}>`
  width: 100%;
  height: ${(props): string => (props.mapHeight ? props.mapHeight : '50vh')};
  transition: all 1s ease-in-out;
`

const StyledHelpButton = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`

const HelpContainer = styled.div<{ height?: string }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: ${(props): string => (props.height ? props.height : '50vh')};
`

/** ポップオーバー */
const PopoverArea = styled(Text)<{
  arrow?: 'top' | 'right'
  marginTop?: number
  marginRight?: number
  marginBottom?: number
  marginLeft?: number
  blowingTop?: number
}>`
  display: flex;
  margin-top: ${(props): string =>
    props.marginTop ? `${props.marginTop}px` : 'auto'};
  margin-right: ${(props): string =>
    props.marginRight ? `${props.marginRight}px` : '16px'};
  margin-bottom: ${(props): string =>
    props.marginBottom ? `${props.marginBottom}px` : 'auto'};
  margin-left: ${(props): string =>
    props.marginLeft ? `${props.marginLeft}px` : '16px'};
  padding: 12px;
  font-size: 14px;
  line-height: 2em;
  background: #ffffff;
  border-radius: 6px;

  ${(props): string =>
    props.arrow === 'top'
      ? `
    &:before {
      position: absolute;
      top: -4px;
      left: 10%;
      margin-left: -8px;
      border: 8px solid transparent;
      border-bottom: 14px solid #FFFFFF;
      content: '';
    }
  `
      : props.arrow === 'right'
      ? `&:after {
          position: relative;
          top: 30%;
          right: -34px;
          height: 0;
          border: 8px solid transparent;
          border-left: 14px solid #FFFFFF;
          content: '';
        }`
      : `&:after {
      position: relative;
      top: ${props.blowingTop}%;
      right: 75%;
      margin-left: -8px;
      border: 8px solid transparent;
      border-bottom: 14px solid #FFFFFF;
      content: '';
      rotate: 60deg;`}
`

const HelpFooter = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`

const RedText = styled.span`
  color: Red;
`

export default MapArea
